import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDefaultGraph } from './data/sampleGraphs';
import { useAlgorithm } from './hooks/useAlgorithm';
import { useGraphData } from './hooks/useGraphData';
import { useGraphEditing } from './hooks/useGraphEditing';
import { usePathBuilding } from './hooks/usePathBuilding';
import AlgorithmTab from './components/AlgorithmTab/AlgorithmTab';
import CustomerPathTab from './components/CustomerPathTab/CustomerPathTab';
import GraphCustomizeTab from './components/GraphCustomizeTab/GraphCustomizeTab';
import GraphVisualizer from './components/shared/GraphVisualizer';
import PathDisplay from './components/AlgorithmTab/PathDisplay';
import './styles/ShortestPathPage.css';

/**
 * Shortest Path Page Component - JavaScript Version
 * 
 * Main interface for the shortest path module. Provides:
 * - Graph visualization area
 * - Control panel for algorithm selection
 * - Path display and analysis
 * - Tab-based navigation between different modes
 */
const ShortestPathPage = () => {
  const navigate = useNavigate();
  
  // Graph data and state management with lazy initialization
  const {
    graphData,
    setGraphData,
    graphVersion,
    setGraphVersion,
    resetGraph
  } = useGraphData(() => getDefaultGraph());

  // Algorithm execution state
  const {
    isLoading,
    error,
    result,
    currentStep,
    totalSteps,
    runAlgorithm,
    nextStep,
    previousStep,
    goToStep,
    getCurrentStep,
    reset
  } = useAlgorithm();

  // Path building state
  const {
    customStartNode,
    setCustomStartNode,
    customEndNode,
    setCustomEndNode,
    customPath,
    setCustomPath,
    isBuildingPath,
    setIsBuildingPath,
    optimalPath,
    setOptimalPath,
    showOptimalPath,
    setShowOptimalPath
  } = usePathBuilding();

  // Graph editing state
  const {
    nextNodeId,
    setNextNodeId,
    edgeForm,
    setEdgeForm,
    addNode,
    addEdge,
    deleteNode,
    deleteNodeWithConfirmation,
    exportGraph,
    importGraph
  } = useGraphEditing({ graphData, setGraphData, setGraphVersion });

  // UI state
  const [activeTab, setActiveTab] = useState('algorithm');
  const [selectedPath, setSelectedPath] = useState([]);
  const [startNode, setStartNode] = useState(null);
  const [endNode, setEndNode] = useState(null);

  // Handle node clicks based on active tab
  const handleNodeClick = (node) => {
    if (activeTab === 'custom' && isBuildingPath) {
      addNodeToPath(node.id);
      return;
    }
    
    // Original algorithm mode logic
    if (!startNode) {
      setStartNode(node);
      setSelectedPath([node.id]);
    } else if (!endNode && node.id !== startNode.id) {
      setEndNode(node);
      setSelectedPath([startNode.id, node.id]);
    } else {
      // Reset selection
      setStartNode(null);
      setEndNode(null);
      setSelectedPath([]);
    }
  };

  // Custom path helper functions
  // Check if nodes are connected
  const areNodesConnected = (node1Id, node2Id) => {
    return graphData.edges.some(edge => 
      (edge.from_ === node1Id && edge.to === node2Id) ||
      (edge.from_ === node2Id && edge.to === node1Id)
    );
  };

  // Get connected nodes
  const getConnectedNodes = (nodeId) => {
    return graphData.edges
      .filter(edge => edge.from_ === nodeId || edge.to === nodeId)
      .map(edge => edge.from_ === nodeId ? edge.to : edge.from_);
  };

  // Add node to path
  const addNodeToPath = (nodeId) => {
    if (!isBuildingPath) return;
    
    const lastNodeId = customPath[customPath.length - 1];
    
    // Check if the node is connected to the last node in the path
    if (!areNodesConnected(lastNodeId, nodeId)) {
      const node = graphData.nodes.find(n => n.id === nodeId);
      alert(`Node ${node?.label} is not connected to the last node in your path.`);
      return;
    }
    
    // Check if we've reached the end node
    if (nodeId === customEndNode.id) {
      setCustomPath([...customPath, nodeId]);
      setIsBuildingPath(false);
      alert('Path completed!');
      return;
    }
    
    // Add node to path
    setCustomPath([...customPath, nodeId]);
  };

  // Calculate optimal path using Dijkstra's algorithm
  const calculateOptimalPath = async () => {
    if (!customStartNode || !customEndNode) return;
    
    try {
      const result = await runAlgorithm(
        'dijkstra',
        graphData.nodes,
        graphData.edges,
        customStartNode.id,
        customEndNode.id
      );
      
      console.log('Algorithm result:', result);
      console.log('Result steps:', result.steps);
      if (result.steps && result.steps.length > 0) {
        console.log('First step:', result.steps[0]);
      }
      
      if (result.success) {
        // Set optimal path
        setOptimalPath(result.path);
      } else {
        // Set optimal path to empty array
        setOptimalPath([]);
      }
    } catch (err) {
      console.error('Failed to calculate optimal path:', err);
      setOptimalPath([]);
    }
  };

  // Calculate optimal path when start and end nodes are selected
  useEffect(() => {
    if (customStartNode && customEndNode) {
      calculateOptimalPath();
    } else {
      setOptimalPath([]);
    }
  }, [customStartNode, customEndNode, graphData]);

  // Initialize nextNodeId based on current graph data
  useEffect(() => {
    if (graphData.nodes.length > 0) {
      const maxId = Math.max(...graphData.nodes.map(n => n.id));
      setNextNodeId(maxId + 1);
    } else {
      setNextNodeId(1);
    }
  }, [graphData.nodes, setNextNodeId]);

  const handleStepChange = (stepIndex) => {
    goToStep(stepIndex);
  };

  // Update node position in graphData
  const handleNodePositionChange = (nodeId, x, y) => {
    const updatedGraphData = {
      ...graphData,
      nodes: graphData.nodes.map(node =>
        node.id === nodeId ? { ...node, x, y } : node
      )
    };
    setGraphData(updatedGraphData);
  };

  const getCurrentStepData = () => {
    const step = getCurrentStep();
    if (!step) return null;
    
    // Transform AlgorithmStep to StepData format
    return {
      current_node: step.current,
      distances: step.distances,
      visited: step.visited,
      queue: step.queue,
      neighbors_updated: step.neighbors_updated ? step.neighbors_updated
        .filter(nodeId => nodeId !== undefined && nodeId !== null)
        .map(nodeId => ({
          node: nodeId,
          old_distance: 0, // We don't have old distance info in AlgorithmStep
          new_distance: step.distances[nodeId] || 0
        })) : []
    };
  };

  return (
    <div className="shortest-path-page">
      {/* Navigation Header */}
      <div className="navigation-header">
        <button 
          className="nav-back-btn"
          onClick={() => navigate('/simulations')}
        >
          ← Back to Simulations
        </button>
        <button 
          className="nav-home-btn"
          onClick={() => navigate('/')}
        >
          🏠 Home
        </button>
        <button 
          className="nav-reset-btn"
          onClick={resetGraph}
          title="Reset graph to default state"
        >
          🔄 Reset Graph
        </button>
      </div>
      
      <header className="page-header">
        <h1>Shortest Path Finder</h1>
        <p>Visualize and analyze pathfinding algorithms</p>
      </header>
      
      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-btn ${activeTab === 'algorithm' ? 'active' : ''}`}
          onClick={() => setActiveTab('algorithm')}
        >
          Algorithm Mode
        </button>
        <button 
          className={`tab-btn ${activeTab === 'custom' ? 'active' : ''}`}
          onClick={() => setActiveTab('custom')}
        >
          Custom Path
        </button>
        <button 
          className={`tab-btn ${activeTab === 'customize' ? 'active' : ''}`}
          onClick={() => setActiveTab('customize')}
        >
          Graph Customize
        </button>
      </div>
      
      <div className="main-content">
        {activeTab === 'algorithm' && (
          <AlgorithmTab
            graphData={graphData}
            setGraphData={setGraphData}
            selectedPath={selectedPath}
            setSelectedPath={setSelectedPath}
            startNode={startNode}
            setStartNode={setStartNode}
            endNode={endNode}
            setEndNode={setEndNode}
            onNodeClick={handleNodeClick}
            stepData={getCurrentStepData()}
            nodes={graphData.nodes}
            // Pass shared algorithm state
            result={result}
            isLoading={isLoading}
            error={error}
            currentStep={currentStep}
            totalSteps={totalSteps}
            runAlgorithm={runAlgorithm}
            nextStep={nextStep}
            previousStep={previousStep}
            goToStep={goToStep}
            getCurrentStep={getCurrentStep}
            reset={reset}
          />
        )}
        
        {activeTab === 'custom' && (
          <CustomerPathTab
            key={`customer-path-${graphVersion}`}
            graphData={graphData}
            nodes={graphData.nodes}
            onNodeClick={handleNodeClick}
            customStartNode={customStartNode}
            setCustomStartNode={setCustomStartNode}
            customEndNode={customEndNode}
            setCustomEndNode={setCustomEndNode}
            customPath={customPath}
            setCustomPath={setCustomPath}
            isBuildingPath={isBuildingPath}
            setIsBuildingPath={setIsBuildingPath}
            optimalPath={optimalPath}
            setOptimalPath={setOptimalPath}
            showOptimalPath={showOptimalPath}
            setShowOptimalPath={setShowOptimalPath}
          />
        )}
        
        {activeTab === 'customize' && (
          <GraphCustomizeTab
            graphData={graphData}
            setGraphData={setGraphData}
            nextNodeId={nextNodeId}
            setNextNodeId={setNextNodeId}
            edgeForm={edgeForm}
            setEdgeForm={setEdgeForm}
            addNode={addNode}
            addEdge={addEdge}
            deleteNode={deleteNode}
            deleteNodeWithConfirmation={deleteNodeWithConfirmation}
            exportGraph={exportGraph}
            importGraph={importGraph}
          />
        )}
        
        <div className="visualization-area">
        {activeTab === 'algorithm' && (
            <GraphVisualizer
              key={`graph-${graphVersion}-algorithm`}
              nodes={graphData.nodes}
              edges={graphData.edges}
              selectedPath={selectedPath}
              onNodeClick={handleNodeClick}
              stepData={getCurrentStepData()}
              />
            )}

          {activeTab === 'custom' && (
          <GraphVisualizer
            key={`graph-${graphVersion}-custom`}
            nodes={graphData.nodes}
            edges={graphData.edges}
            onNodeClick={handleNodeClick}
            customPath={customPath}
            optimalPath={optimalPath}
            showOptimalPath={showOptimalPath}
          />
          )}

          {activeTab === 'customize' && (
            <GraphVisualizer
              key={`graph-${graphVersion}-customize`}
              nodes={graphData.nodes}
              edges={graphData.edges}
              onNodeClick={handleNodeClick}
            />
          )}
          
          {activeTab === 'algorithm' && result && result.success && (() => {
            console.log('1. steps being passed to PathDisplay:', result.steps);
            console.log('2. currentStep being passed to PathDisplay:', currentStep);
            return (
              <PathDisplay
              path={result.path}
              cost={result.cost}
              algorithm={result.algorithm}
              executionTime={result.executionTime}
              nodes={graphData.nodes}
              showDetails={true}
              
              // Step visualization props
              steps={result.steps}
              currentStep={currentStep}
              totalSteps={result.steps.length}
              onStepChange={handleStepChange}
              showStepVisualization={true}
              onNodeHighlight={(nodeId) => {
                // Highlight node in graph - you can implement this
                console.log('Highlight node:', nodeId);
              }}
              onEdgeHighlight={(from, to) => {
                // Highlight edge in graph - you can implement this
                console.log('Highlight edge:', from, '->', to);
              }}
            />
            );
          })()}
          
        </div>
      </div>
    </div>
  );
};

export default ShortestPathPage;
